import { useState } from "react";
import { toast } from "sonner";
import { X, Check, XCircle, Linkedin, Globe, Mail, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { fmtTimePT, fmtDatePT } from "../lib/time";

export default function CapitalRequestDrawer({ submission, onClose, onDecided }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [showReason, setShowReason] = useState(false);

  if (!submission) return null;
  const s = submission;

  const decide = async (status) => {
    if (status === "declined" && !showReason) { setShowReason(true); return; }
    setBusy(true);
    try {
      await api.post(`/submissions/${s.id}/decide`, { status, reason: reason || "" });
      toast.success(status === "accepted" ? "Approved · slot booked · founder notified" : "Declined · founder notified");
      onDecided?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const link = (v) => v && (v.startsWith("http") ? v : `https://${v}`);
  const isPending = s.status === "pending";

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: "rgba(20,22,28,0.28)" }} onClick={onClose} data-testid="capital-drawer-scrim" />
      <aside className="fixed top-0 right-0 z-50 h-screen w-full sm:w-[520px] flex flex-col" data-testid="capital-request-drawer">
        <div className="glass-panel h-full flex flex-col rounded-none sm:rounded-l-3xl overflow-hidden" style={{ background: "hsl(var(--surface) / 0.95)" }}>
          <div className="px-6 pt-6 pb-4 border-b border-white/50 shrink-0 flex items-start justify-between gap-3">
            <div>
              <div className="overline">Founder Labs request</div>
              <div className="font-heading text-xl mt-1">{s.name || "Founder"}</div>
              <div className="text-xs text-muted-foreground font-mono mt-0.5">{fmtDatePT(s.slot_start_at, { weekday: "short", month: "short", day: "numeric" })} · {fmtTimePT(s.slot_start_at)} – {fmtTimePT(s.slot_end_at)} PT</div>
              <div className="mt-2">
                {s.status === "pending" ? <span className="chip chip-cream-2 text-[10px]">Pending review</span>
                  : s.status === "accepted" ? <span className="chip chip-green text-[10px]">Accepted</span>
                  : <span className="chip text-[10px]">{s.status}</span>}
              </div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-white/60 flex items-center justify-center shrink-0" data-testid="capital-drawer-close"><X size={16} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div className="glass-panel p-4" data-testid="request-contact">
              <div className="overline mb-3">Contact</div>
              <div className="space-y-2 text-sm">
                {s.email && (
                  <a href={`mailto:${s.email}`} className="flex items-center gap-2 hover:underline">
                    <Mail size={12} style={{ color: "hsl(var(--vd-green))" }} />
                    <span className="font-mono">{s.email}</span>
                  </a>
                )}
                {s.linkedin && (
                  <a href={link(s.linkedin)} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline text-muted-foreground">
                    <Linkedin size={12} style={{ color: "hsl(var(--vd-green))" }} />
                    <span className="truncate">{s.linkedin}</span>
                  </a>
                )}
                {s.website && (
                  <a href={link(s.website)} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline text-muted-foreground">
                    <Globe size={12} style={{ color: "hsl(var(--vd-green))" }} />
                    <span className="truncate">{s.website}</span>
                  </a>
                )}
              </div>
            </div>

            {s.pitch_note && (
              <div className="glass-panel p-4" data-testid="request-pitch">
                <div className="flex items-center gap-2 mb-2"><MessageCircle size={12} style={{ color: "hsl(var(--vd-green))" }} /><span className="overline">What they&apos;re working on</span></div>
                <div className="text-sm leading-relaxed italic">&ldquo;{s.pitch_note}&rdquo;</div>
              </div>
            )}

            {s.created_at && (
              <div className="text-[11px] text-muted-foreground font-mono" data-testid="request-meta">
                Submitted {fmtDatePT(s.created_at, { month: "short", day: "numeric" })} · {fmtTimePT(s.created_at)}
              </div>
            )}

            {showReason && (
              <div className="glass-panel p-4" data-testid="decline-reason-box">
                <div className="overline mb-2">Reason (optional — included in decline email)</div>
                <textarea rows={3} className="input-field resize-none" placeholder="Not a fit for this investor&apos;s focus this quarter…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="decline-reason" />
              </div>
            )}
          </div>

          {isPending && (
            <div className="px-6 py-4 border-t border-white/50 flex gap-2 shrink-0" data-testid="drawer-actions">
              <button onClick={onClose} className="btn-ghost text-sm" disabled={busy}>Cancel</button>
              <div className="flex-1" />
              {showReason ? (
                <button onClick={() => decide("declined")} disabled={busy} className="btn-primary text-sm" style={{ background: "hsl(var(--destructive))" }} data-testid="capital-decline-confirm">
                  <XCircle size={12} />Confirm decline
                </button>
              ) : (
                <>
                  <button onClick={() => decide("declined")} disabled={busy} className="btn-ghost text-sm hover:text-destructive" data-testid="capital-decline">
                    <XCircle size={12} />Decline
                  </button>
                  <button onClick={() => decide("accepted")} disabled={busy} className="btn-primary text-sm" data-testid="capital-accept">
                    <Check size={12} />Accept
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
